/**
 * PO Parties Fields Component
 *
 * Renders the parties section of the Purchase Order form.
 * Parties can be: VENDOR, BUYER, or CONSIGNEE.
 *
 * Uses react-hook-form's useFieldArray for dynamic array management.
 * Users can add, edit, and remove parties.
 *
 * This maps to:
 * - X12 850: N1 Loop (Name segments)
 * - EDIFACT ORDERS: NAD segments (Name and Address)
 */

import { useFieldArray } from 'react-hook-form';
import Input from '../common/Input';
import Button from '../common/Button';
import { COUNTRY_OPTIONS } from '../../constants/countries';
import { isFieldEmpty, resolveFieldValue, normalizeConfidence, getFieldScore, getFieldIssues, unwrapEvaluation } from '../../utils/confidenceUtils';

/**
 * Severity color map for inline issue indicators on select fields
 */
const SEVERITY_COLORS = {
  error:   { border: 'border-l-red-500',   text: 'text-red-600',   icon: 'text-red-500' },
  warning: { border: 'border-l-amber-500', text: 'text-amber-600', icon: 'text-amber-500' },
  info:    { border: 'border-l-blue-500',  text: 'text-blue-600',  icon: 'text-blue-500' },
};

function formatIssueType(type) {
  if (!type) return 'Issue';
  return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function IssueIndicator({ issues = [] }) {
  if (issues.length === 0) return null;
  const worst = issues.some(i => i.severity === 'error') ? 'error'
    : issues.some(i => i.severity === 'warning') ? 'warning' : 'info';
  return (
    <span className="relative group/issues inline-flex">
      <svg className={`w-3.5 h-3.5 ${SEVERITY_COLORS[worst]?.icon || 'text-amber-500'} cursor-help`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
      <div className="absolute left-0 top-full mt-1 z-50 hidden group-hover/issues:block w-64">
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
          {issues.map((issue, idx) => (
            <div key={idx} className={`px-3 py-2 border-l-4 ${SEVERITY_COLORS[issue.severity]?.border || 'border-l-gray-300'} ${idx > 0 ? 'border-t border-gray-100' : ''}`}>
              <p className={`text-xs font-semibold ${SEVERITY_COLORS[issue.severity]?.text || 'text-gray-600'}`}>{formatIssueType(issue.type)}</p>
              {issue.detail && <p className="text-xs text-gray-600 mt-0.5">{issue.detail}</p>}
            </div>
          ))}
        </div>
      </div>
    </span>
  );
}

/**
 * Party type options
 * These are the standard party types in purchase orders
 */
const PARTY_TYPES = [
  { value: 'VENDOR', label: 'Vendor (Supplier)' },
  { value: 'BUYER', label: 'Buyer (Customer)' },
  { value: 'CONSIGNEE', label: 'Consignee (Delivery Address)' }
];

/**
 * POPartiesFields Component
 *
 * @param {Object} props - Component props
 * @param {Object} props.form - React Hook Form instance
 * @param {Object} props.confidence - Confidence scores from AI extraction
 *
 * @example
 * <POPartiesFields
 *   form={form}
 *   confidence={{ parties: 0.85 }}
 * />
 */
function POPartiesFields({ form, evaluation: rawEvaluation, confidence = {}, issues = [] }) {
  const evaluation = unwrapEvaluation(rawEvaluation);
  // Destructure form methods
  const { register, control, formState: { errors }, watch } = form;

  // useFieldArray manages the dynamic parties array
  // - fields: current array of parties
  // - append: add a new party
  // - remove: remove a party by index
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'parties'
  });

  // Watch the parties array for real-time updates
  const watchedParties = watch('parties');

  /**
   * Add a new empty party
   */
  const addParty = () => {
    append({
      type: 'VENDOR',
      name: '',
      address: {
        addressLine1: '',
        addressLine2: '',
        addressLine3: '',
        city: '',
        state: '',
        postalCode: '',
        country: ''
      },
      id_code: '',
      contactName: '',
      contactDepartment: '',
      phone: '',
      email: ''
    });
  };

  /**
   * Get per-field score for a specific party.
   * Checks LLM-as-judge evaluation first (paths like "parties[0].name"),
   * then falls back to legacy confidence format.
   * @param {number} index - Party index
   * @param {string} fieldName - Field name (e.g. 'name', 'address.city', 'contactName')
   * @returns {number|undefined} Score or undefined
   */
  const getPartyConfidence = (index, fieldName) => {
    // Try new evaluation format first — field paths like "parties[0].name"
    const evalScore = getFieldScore(evaluation, null, `parties[${index}].${fieldName}`);
    if (evalScore != null) return evalScore;

    // Fall back to legacy confidence format
    const partiesConf = confidence.parties;
    let conf;
    // Legacy array format: array of per-field objects
    if (Array.isArray(partiesConf) && partiesConf[index]) {
      conf = partiesConf[index][fieldName];
    }
    // Legacy scalar format: single number for all fields
    else if (typeof partiesConf === 'number') {
      conf = partiesConf;
    }
    if (conf === undefined) return undefined;
    // Look up the actual field value — handle dot-notation keys like 'address.city'
    const partyData = watchedParties?.[index];
    const val = resolveFieldValue(partyData, fieldName);
    // Normalize legacy 0.0-on-empty to 0.95 (both mean "confidently absent")
    return normalizeConfidence(conf, isFieldEmpty(val));
  };

  /**
   * Check if a specific party has any low-score field.
   * Checks evaluation field_scores first, then legacy confidence.
   */
  const isPartyLowConfidence = (index) => {
    // Check evaluation field_scores for any low-scoring party field
    if (evaluation?.field_scores) {
      const prefix = `parties[${index}].`;
      return Object.entries(evaluation.field_scores).some(([path, fieldData]) => {
        if (!path.startsWith(prefix)) return false;
        const score = typeof fieldData === 'object' ? fieldData.score : fieldData;
        return score != null && score < 0.8;
      });
    }

    // Fall back to legacy confidence
    const partiesConf = confidence.parties;
    if (Array.isArray(partiesConf) && partiesConf[index]) {
      const partyData = watchedParties?.[index];
      return Object.entries(partiesConf[index]).some(([key, conf]) => {
        if (typeof conf !== 'number') return false;
        const val = resolveFieldValue(partyData, key);
        return normalizeConfidence(conf, isFieldEmpty(val)) < 0.8;
      });
    }
    if (typeof partiesConf === 'number') {
      return partiesConf < 0.8;
    }
    return false;
  };

  /**
   * Compute aggregate score % for the section header.
   * Checks evaluation field_scores first, then legacy confidence.
   * Uses min across all party fields.
   */
  const getAggregateConfidence = () => {
    // Check evaluation field_scores for party fields
    if (evaluation?.field_scores) {
      let min = 1;
      let hasRelevant = false;
      for (const [path, fieldData] of Object.entries(evaluation.field_scores)) {
        if (!path.startsWith('parties[')) continue;
        const score = typeof fieldData === 'object' ? fieldData.score : fieldData;
        if (score == null) continue;
        hasRelevant = true;
        if (score < min) min = score;
      }
      return hasRelevant ? min : undefined;
    }

    // Fall back to legacy confidence
    const partiesConf = confidence.parties;
    if (Array.isArray(partiesConf) && partiesConf.length > 0) {
      let min = 1;
      let hasRelevant = false;
      partiesConf.forEach((partyObj, partyIndex) => {
        if (!partyObj || typeof partyObj !== 'object') return;
        const partyData = watchedParties?.[partyIndex];
        for (const [key, conf] of Object.entries(partyObj)) {
          if (typeof conf !== 'number') continue;
          const val = resolveFieldValue(partyData, key);
          const normalized = normalizeConfidence(conf, isFieldEmpty(val));
          hasRelevant = true;
          if (normalized < min) min = normalized;
        }
      });
      return hasRelevant ? min : undefined;
    }
    if (typeof partiesConf === 'number') return partiesConf;
    return undefined;
  };

  const aggregateConfidence = getAggregateConfidence();
  const hasPartiesConfidence = aggregateConfidence !== undefined;
  const hasLowConfidence = hasPartiesConfidence && aggregateConfidence < 0.8;

  return (
    <div className="space-y-4">
      {/* Section Title with confidence indicator */}
      <div className="flex items-center justify-between border-b pb-2">
        <h3 className="text-lg font-medium text-gray-900">
          Parties
          {hasPartiesConfidence && (
            <span className={`ml-2 text-sm font-normal ${hasLowConfidence ? 'text-yellow-600' : 'text-green-600'}`}>
              ({Math.round(aggregateConfidence * 100)}%)
            </span>
          )}
        </h3>
        <Button variant="secondary" size="sm" onClick={addParty}>
          + Add Party
        </Button>
      </div>

      {/* Empty state */}
      {fields.length === 0 && (
        <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-md">
          No parties added yet. Click "Add Party" to add vendor, buyer, or ship-to information.
        </div>
      )}

      {/* Party entries */}
      {fields.map((field, index) => (
        <div
          key={field.id}
          className={`
            border rounded-lg p-4 space-y-3
            ${isPartyLowConfidence(index) ? 'border-amber-400 bg-amber-50' : 'border-gray-200'}
          `}
        >
          {/* Party header with remove button */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              Party #{index + 1}
            </span>
            <button
              type="button"
              onClick={() => remove(index)}
              className="text-red-500 hover:text-red-700 text-sm"
            >
              Remove
            </button>
          </div>

          {/* Party fields grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Party Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <span className="inline-flex items-center gap-1">
                  Type
                  {getPartyConfidence(index, 'type') !== undefined && (
                    <span className={`text-xs ${getPartyConfidence(index, 'type') >= 0.8 ? 'text-green-600' : 'text-yellow-600'}`}>
                      ({Math.round(getPartyConfidence(index, 'type') * 100)}%)
                    </span>
                  )}
                  <IssueIndicator issues={getFieldIssues(issues, `parties[${index}].type`)} />
                </span>
              </label>
              <select
                {...register(`parties.${index}.type`)}
                defaultValue={field.type}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  getPartyConfidence(index, 'type') !== undefined && getPartyConfidence(index, 'type') < 0.8
                    ? 'border-amber-400 bg-amber-50' : 'border-gray-300'
                }`}
              >
                {PARTY_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* ID Code (Tax ID, DUNS, etc.) */}
            <Input
              label="ID Code (Tax ID/DUNS)"
              {...register(`parties.${index}.id_code`)}
              defaultValue={field.id_code}
              error={errors.parties?.[index]?.id_code}
              confidence={getPartyConfidence(index, 'id_code')}
              issues={getFieldIssues(issues, `parties[${index}].id_code`)}
              placeholder="e.g., 123456789"
            />

            {/* Name - full width */}
            <div className="md:col-span-2">
              <Input
                label="Name"
                {...register(`parties.${index}.name`, {
                  required: 'Party name is required'
                })}
                defaultValue={field.name}
                error={errors.parties?.[index]?.name}
                confidence={getPartyConfidence(index, 'name')}
                issues={getFieldIssues(issues, `parties[${index}].name`)}
                placeholder="Company or person name"
              />
            </div>

            {/* Address Line 1 - full width */}
            <div className="md:col-span-2">
              <Input
                label="Address Line 1"
                {...register(`parties.${index}.address.addressLine1`)}
                defaultValue={field.address?.addressLine1}
                error={errors.parties?.[index]?.address?.addressLine1}
                confidence={getPartyConfidence(index, 'address.addressLine1')}
                issues={getFieldIssues(issues, `parties[${index}].address.addressLine1`)}
                placeholder="Address line 1 (street address, P.O. box, etc.)"
              />
            </div>

            {/* Address Line 2 - full width */}
            <div className="md:col-span-2">
              <Input
                label="Address Line 2"
                {...register(`parties.${index}.address.addressLine2`)}
                defaultValue={field.address?.addressLine2}
                error={errors.parties?.[index]?.address?.addressLine2}
                confidence={getPartyConfidence(index, 'address.addressLine2')}
                issues={getFieldIssues(issues, `parties[${index}].address.addressLine2`)}
                placeholder="Address line 2 (suite, building, floor, etc.)"
              />
            </div>

            {/* Address Line 3 - full width */}
            <div className="md:col-span-2">
              <Input
                label="Address Line 3"
                {...register(`parties.${index}.address.addressLine3`)}
                defaultValue={field.address?.addressLine3}
                error={errors.parties?.[index]?.address?.addressLine3}
                confidence={getPartyConfidence(index, 'address.addressLine3')}
                issues={getFieldIssues(issues, `parties[${index}].address.addressLine3`)}
                placeholder="Address line 3 (optional)"
              />
            </div>

            {/* City */}
            <Input
              label="City"
              {...register(`parties.${index}.address.city`)}
              defaultValue={field.address?.city}
              error={errors.parties?.[index]?.address?.city}
              confidence={getPartyConfidence(index, 'address.city')}
              issues={getFieldIssues(issues, `parties[${index}].address.city`)}
              placeholder="City"
            />

            {/* State/Province */}
            <Input
              label="State/Province"
              {...register(`parties.${index}.address.state`)}
              defaultValue={field.address?.state}
              error={errors.parties?.[index]?.address?.state}
              confidence={getPartyConfidence(index, 'address.state')}
              issues={getFieldIssues(issues, `parties[${index}].address.state`)}
              placeholder="State or Province"
            />

            {/* Postal Code */}
            <Input
              label="Postal Code"
              {...register(`parties.${index}.address.postalCode`)}
              defaultValue={field.address?.postalCode}
              error={errors.parties?.[index]?.address?.postalCode}
              confidence={getPartyConfidence(index, 'address.postalCode')}
              issues={getFieldIssues(issues, `parties[${index}].address.postalCode`)}
              placeholder="Postal Code"
            />

            {/* Country */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <span className="inline-flex items-center gap-1">
                  Country
                  {getPartyConfidence(index, 'address.country') !== undefined && (
                    <span className={`text-xs ${getPartyConfidence(index, 'address.country') >= 0.8 ? 'text-green-600' : 'text-yellow-600'}`}>
                      ({Math.round(getPartyConfidence(index, 'address.country') * 100)}%)
                    </span>
                  )}
                  <IssueIndicator issues={getFieldIssues(issues, `parties[${index}].address.country`)} />
                </span>
              </label>
              <select
                {...register(`parties.${index}.address.country`)}
                defaultValue={field.address?.country}
                className={`w-full px-3 py-2 border rounded-md shadow-sm text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  getPartyConfidence(index, 'address.country') !== undefined && getPartyConfidence(index, 'address.country') < 0.8
                    ? 'border-amber-400 bg-amber-50' : 'border-gray-300'
                }`}
              >
                {COUNTRY_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Contact Information Section */}
          <div className="border-t pt-3 mt-3">
            <h4 className="text-sm font-medium text-gray-600 mb-2">Contact Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Contact Name */}
              <Input
                label="Contact Name"
                {...register(`parties.${index}.contactName`)}
                defaultValue={field.contactName}
                error={errors.parties?.[index]?.contactName}
                confidence={getPartyConfidence(index, 'contactName')}
                issues={getFieldIssues(issues, `parties[${index}].contactName`)}
                placeholder=""
              />

              {/* Contact Department */}
              <Input
                label="Department"
                {...register(`parties.${index}.contactDepartment`)}
                defaultValue={field.contactDepartment}
                error={errors.parties?.[index]?.contactDepartment}
                confidence={getPartyConfidence(index, 'contactDepartment')}
                issues={getFieldIssues(issues, `parties[${index}].contactDepartment`)}
                placeholder=""
              />

              {/* Phone */}
              <Input
                label="Phone"
                {...register(`parties.${index}.phone`)}
                defaultValue={field.phone}
                error={errors.parties?.[index]?.phone}
                confidence={getPartyConfidence(index, 'phone')}
                issues={getFieldIssues(issues, `parties[${index}].phone`)}
                placeholder=""
              />

              {/* Email */}
              <Input
                label="Email"
                {...register(`parties.${index}.email`)}
                defaultValue={field.email}
                error={errors.parties?.[index]?.email}
                confidence={getPartyConfidence(index, 'email')}
                issues={getFieldIssues(issues, `parties[${index}].email`)}
                placeholder=""
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default POPartiesFields;
